import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Loader2, CreditCard, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react'
import { getRazorpayConfig, verifySubscription } from '@/api/payments'

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance
  }
}

interface RazorpayOptions {
  key: string
  subscription_id: string
  name: string
  description: string
  handler: (response: RazorpayResponse) => void
  modal: {
    ondismiss: () => void
  }
  theme: {
    color: string
  }
}

interface RazorpayInstance {
  open: () => void
  close: () => void
  on: (event: string, callback: (response: RazorpayPaymentFailedResponse) => void) => void
}

interface RazorpayResponse {
  razorpay_payment_id: string
  razorpay_subscription_id: string
  razorpay_signature: string
}

interface RazorpayPaymentFailedResponse {
  error: {
    code: string
    description: string
    source: string
    step: string
    reason: string
    metadata?: Record<string, string>
  }
}

type PaymentStatus = 'loading' | 'ready' | 'processing' | 'success' | 'failed' | 'cancelled'

function extractApiError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { detail?: string; message?: string } }; message?: string }
  return e?.response?.data?.detail || e?.response?.data?.message || e?.message || fallback
}

export default function PaymentGatewayPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const subscriptionId = searchParams.get('subscription_id') || ''

  const [status, setStatus] = useState<PaymentStatus>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [rzpKey, setRzpKey] = useState<string | null>(null)
  const [scriptReady, setScriptReady] = useState<boolean>(typeof window !== 'undefined' && !!window.Razorpay)
  const rzpInstanceRef = useRef<RazorpayInstance | null>(null)

  // Validate subscription_id present
  useEffect(() => {
    if (!subscriptionId) {
      setStatus('failed')
      setErrorMsg('No subscription ID provided. Please return to the plan selector and try again.')
    }
  }, [subscriptionId])

  // Fetch the Razorpay public key
  useEffect(() => {
    if (!subscriptionId) return
    let cancelled = false
    getRazorpayConfig()
      .then((cfg) => {
        if (cancelled) return
        if (!cfg?.key_id) {
          setStatus('failed')
          setErrorMsg('Payment gateway is not configured. Contact support.')
          return
        }
        setRzpKey(cfg.key_id)
      })
      .catch((err) => {
        if (cancelled) return
        setStatus('failed')
        setErrorMsg(extractApiError(err, 'Could not retrieve payment gateway config. Please try again.'))
      })
    return () => { cancelled = true }
  }, [subscriptionId])

  // Load Razorpay checkout script (idempotent: reuse existing tag if present)
  useEffect(() => {
    if (!subscriptionId) return
    if (window.Razorpay) {
      setScriptReady(true)
      return
    }
    const SRC = 'https://checkout.razorpay.com/v1/checkout.js'
    let script = document.querySelector<HTMLScriptElement>(`script[src="${SRC}"]`)
    let appended = false
    if (!script) {
      script = document.createElement('script')
      script.src = SRC
      script.async = true
      document.body.appendChild(script)
      appended = true
    }
    const onLoad = () => setScriptReady(true)
    const onError = () => {
      setStatus('failed')
      setErrorMsg('Failed to load Razorpay checkout. Check your network connection and try again.')
    }
    script.addEventListener('load', onLoad)
    script.addEventListener('error', onError)
    return () => {
      script?.removeEventListener('load', onLoad)
      script?.removeEventListener('error', onError)
      // Only remove the tag we appended — and only if it never finished loading,
      // so subsequent mounts don't have to re-download it.
      if (appended && script && !window.Razorpay && script.parentNode) {
        script.parentNode.removeChild(script)
      }
    }
  }, [subscriptionId])

  // Once both key and script are ready, transition to 'ready'
  useEffect(() => {
    if (status === 'loading' && rzpKey && scriptReady) {
      setStatus('ready')
    }
  }, [status, rzpKey, scriptReady])

  const handlePayment = useCallback(() => {
    if (!window.Razorpay) {
      setStatus('failed')
      setErrorMsg('Payment gateway not yet loaded. Please wait a moment and retry.')
      return
    }
    if (!rzpKey) {
      setStatus('failed')
      setErrorMsg('Payment gateway key unavailable. Please retry.')
      return
    }

    // NOTE: do not flip to 'processing' here — that state is reserved for after Razorpay
    // returns a successful payment and we are verifying it server-side. Keep 'ready' so
    // a Razorpay init failure (e.g. bad key) leaves the user with a Try Again button
    // instead of a stuck spinner.

    const options: RazorpayOptions = {
      key: rzpKey,
      subscription_id: subscriptionId,
      name: 'Twasta.ai',
      description: 'Pro Plan Subscription',
      handler: async (response: RazorpayResponse) => {
        setStatus('processing')
        try {
          await verifySubscription({
            subscription_id: response.razorpay_subscription_id,
            payment_id: response.razorpay_payment_id,
            payment_signature: response.razorpay_signature,
          })
          setStatus('success')
          toast.success('Payment verified successfully!')
          setTimeout(() => {
            navigate('/workbench')
          }, 2000)
        } catch (err) {
          setStatus('failed')
          setErrorMsg(extractApiError(err, 'Payment signature verification failed. If the amount was deducted, please contact support.'))
        }
      },
      modal: {
        ondismiss: () => {
          // Only treat as 'cancelled' if we are still waiting for the user (not already in success/failed)
          setStatus((prev) => (prev === 'success' || prev === 'failed' ? prev : 'cancelled'))
        },
      },
      theme: {
        color: '#0061D0',
      },
    }

    try {
      const rzp = new window.Razorpay(options)
      rzpInstanceRef.current = rzp
      // Razorpay fires 'payment.failed' with details when the gateway itself rejects
      // (bad key, network, declined card, etc.). Without this listener those errors
      // leave the modal in an opaque state and our UI stuck on the prior status.
      rzp.on('payment.failed', (resp) => {
        const desc = resp?.error?.description || resp?.error?.reason || 'Payment failed'
        setStatus('failed')
        setErrorMsg(desc)
        try { rzp.close() } catch { /* ignore */ }
      })
      rzp.open()
    } catch (err) {
      setStatus('failed')
      setErrorMsg(extractApiError(err, 'Failed to initialize payment. Please try again.'))
    }
  }, [subscriptionId, navigate, rzpKey])

  // Auto-open payment when ready
  useEffect(() => {
    if (status === 'ready' && subscriptionId && rzpKey) {
      // Small delay to allow the UI to render
      const timer = setTimeout(handlePayment, 500)
      return () => clearTimeout(timer)
    }
  }, [status, subscriptionId, rzpKey, handlePayment])

  return (
    <div style={{
      maxWidth: 480,
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 200px)',
      textAlign: 'center',
    }}>
      {status === 'loading' && (
        <div className="card" style={{ padding: '3rem 2rem', width: '100%' }}>
          <Loader2 size={48} style={{
            animation: 'spin 1s linear infinite',
            color: 'var(--color-accent)',
            margin: '0 auto 1.5rem',
          }} />
          <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Loading Payment Gateway
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            Setting up secure payment...
          </p>
        </div>
      )}

      {status === 'ready' && (
        <div className="card" style={{ padding: '3rem 2rem', width: '100%' }}>
          <CreditCard size={48} style={{
            color: 'var(--color-accent)',
            margin: '0 auto 1.5rem',
          }} />
          <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Complete Your Payment
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
            You will be redirected to the secure Razorpay checkout.
          </p>
          <button
            className="btn btn-primary btn-lg"
            onClick={handlePayment}
          >
            <CreditCard size={18} />
            Pay Now
          </button>
        </div>
      )}

      {status === 'processing' && (
        <div className="card" style={{ padding: '3rem 2rem', width: '100%' }}>
          <Loader2 size={48} style={{
            animation: 'spin 1s linear infinite',
            color: 'var(--color-accent)',
            margin: '0 auto 1.5rem',
          }} />
          <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Processing Payment
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            Verifying your payment... Please do not close this window.
          </p>
        </div>
      )}

      {status === 'success' && (
        <div className="card" style={{ padding: '3rem 2rem', width: '100%' }}>
          <CheckCircle2 size={48} style={{
            color: 'var(--color-success, #1a7f37)',
            margin: '0 auto 1.5rem',
          }} />
          <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-success, #1a7f37)' }}>
            Payment Successful!
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
            Your Pro plan is now active. Redirecting to workbench...
          </p>
          <div style={{
            width: '100%',
            height: 4,
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-full)',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              background: '#4caf50',
              borderRadius: 'var(--radius-full)',
              animation: 'skeleton-loading 1s linear',
              width: '100%',
            }} />
          </div>
        </div>
      )}

      {status === 'failed' && (
        <div className="card" style={{ padding: '3rem 2rem', width: '100%' }}>
          <XCircle size={48} style={{
            color: 'var(--color-error)',
            margin: '0 auto 1.5rem',
          }} />
          <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-error, #cf222e)' }}>
            Payment Failed
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
            {errorMsg || 'Something went wrong with your payment. Please try again.'}
          </p>
          <div className="flex items-center gap-3" style={{ justifyContent: 'center' }}>
            <button
              className="btn btn-ghost"
              onClick={() => navigate('/payments/plans')}
            >
              <ArrowLeft size={16} />
              Back to Plans
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setStatus('ready')
                setErrorMsg('')
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {status === 'cancelled' && (
        <div className="card" style={{ padding: '3rem 2rem', width: '100%' }}>
          <XCircle size={48} style={{
            color: 'var(--color-warning)',
            margin: '0 auto 1.5rem',
          }} />
          <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Payment Cancelled
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
            You cancelled the payment. You can try again or go back to plan selection.
          </p>
          <div className="flex items-center gap-3" style={{ justifyContent: 'center' }}>
            <button
              className="btn btn-ghost"
              onClick={() => navigate('/payments/plans')}
            >
              <ArrowLeft size={16} />
              Back to Plans
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setStatus('ready')
                handlePayment()
              }}
            >
              <CreditCard size={16} />
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Secure payment notice */}
      <div style={{
        marginTop: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: 'var(--color-text-secondary)',
        fontSize: '0.8125rem',
      }}>
        <span className="material-icons" style={{ fontSize: 15 }}>lock</span>
        Payments are processed securely by Razorpay
      </div>
    </div>
  )
}
